import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";

export type AndroidMidiPort = {
  number: number;
  type: "input" | "output";
  name: string;
  opened: boolean;
};

export type AndroidMidiDevice = {
  id: string;
  name: string;
  manufacturer: string;
  product: string;
  deviceType: "usb" | "bluetooth" | "virtual" | "unknown";
  inputPorts: number;
  outputPorts: number;
  connected: boolean;
  ports: AndroidMidiPort[];
};

export type AndroidMidiDiagnostics = {
  midiFeature: boolean;
  usbHost: boolean;
  midiManagerAvailable: boolean;
  usbDeviceCount: number;
  midiDeviceCount: number;
  stage: "no-midi-feature" | "no-midi-manager" | "no-usb-host" | "no-usb-device" | "usb-device-not-midi" | "midi-device-no-output-port" | "opening-midi-device" | "midi-device-not-open" | "output-port-open-failed" | "output-port-opened-waiting-bytes" | "raw-bytes-no-note-event" | "note-event-pending-ui" | "note-event-received-by-ui";
  lastPort: string;
  lastRawMidi: string;
  lastNoteEvent: string;
  lastUiEvent: string;
  receivedByteCount: number;
  noteEventCount: number;
  uiEventCount: number;
};

export type AndroidMidiState = {
  supported: boolean;
  state: "unsupported" | "disconnected" | "connecting" | "connected" | "error";
  devices: AndroidMidiDevice[];
  diagnostics: AndroidMidiDiagnostics;
  error?: string;
};

export type AndroidMidiMessage = {
  kind: "on" | "off";
  noteNumber: number;
  velocity: number;
  channel: number;
  timestampNanos: number;
  deviceId: string;
  deviceName: string;
};

type AndroidMidiPlugin = {
  getStatus(): Promise<AndroidMidiState>;
  refresh(): Promise<AndroidMidiState>;
  acknowledgeUiEvent(options: { kind: "on" | "off"; noteNumber: number }): Promise<AndroidMidiDiagnostics>;
  addListener(eventName: "midiState", listener: (state: AndroidMidiState) => void): Promise<PluginListenerHandle>;
  addListener(eventName: "midiDiagnostic", listener: (diagnostics: AndroidMidiDiagnostics) => void): Promise<PluginListenerHandle>;
  addListener(eventName: "midiMessage", listener: (message: AndroidMidiMessage) => void): Promise<PluginListenerHandle>;
};

export const AndroidMidi = registerPlugin<AndroidMidiPlugin>("AndroidMidi");

export function hasAndroidMidiBridge(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android" && Capacitor.isPluginAvailable("AndroidMidi");
}